import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "pandax_install_dismissed_at";
const DISMISS_COOLDOWN_MS = 7 * 24 * 3600 * 1000; // 7 days

/**
 * InstallPrompt — floating bottom banner that guides users through installing
 * Panda X to their home screen.
 *
 * • Android/desktop Chrome: listens for `beforeinstallprompt`, exposes a
 *   one-tap "Install" button that fires `event.prompt()`.
 * • iOS Safari: no native prompt exists, so we show a manual "Share → Add to
 *   Home Screen" guide since that's the only path to enable push on iOS.
 * • Dismissals persist 7 days in localStorage so we don't nag on every load.
 * • Hides itself entirely when running in standalone mode (already installed).
 */
export default function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Already installed → hide entirely. `display-mode: standalone` covers
    // Android/desktop; `navigator.standalone` covers iOS Home-Screen apps.
    const standalone = window.matchMedia?.("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    if (standalone) return;
    // Inside the native iOS/Android app there is nothing to "install".
    if (window.Capacitor?.isNativePlatform?.()) return;
    // Respect a recent dismissal.
    try {
      const last = parseInt(window.localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (last && Date.now() - last < DISMISS_COOLDOWN_MS) return;
    } catch { /* private mode */ }

    const ios = /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    // iOS: just show the manual guide after a small delay so we don't
    // interrupt the first-paint.
    if (ios) {
      const t = setTimeout(() => setVisible(true), 3000);
      return () => { window.removeEventListener("beforeinstallprompt", onBeforeInstall); clearTimeout(t); };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = () => {
    try { window.localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setVisible(false);
    setShowIOSGuide(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* user canceled */ }
    setDeferred(null);
    dismiss();
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-3 bottom-20 lg:bottom-3 z-[110] max-w-md lg:left-auto lg:right-3"
      data-testid="pwa-install-prompt"
    >
      <div className="bg-gradient-to-br from-rose-950/95 to-black/95 backdrop-blur-xl border border-rose-500/30 rounded-lg shadow-2xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-lg bg-rose-600 flex items-center justify-center shrink-0 text-white font-bold text-xl">
            P
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white text-sm font-semibold">Install Panda X</div>
            <div className="text-zinc-400 text-xs leading-relaxed mt-0.5">
              {isIOS
                ? "Add to Home Screen so notifications work + you get a full-screen app icon."
                : "One tap gets you a home-screen icon and enables push notifications."}
            </div>
          </div>
          <button
            onClick={dismiss}
            className="text-zinc-500 hover:text-white -mr-1 -mt-1 shrink-0"
            data-testid="pwa-install-dismiss"
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        </div>

        {isIOS ? (
          <>
            <button
              onClick={() => setShowIOSGuide((v) => !v)}
              className="mt-3 w-full bg-white text-black font-semibold rounded-md py-2 text-sm flex items-center justify-center gap-2"
              data-testid="pwa-install-ios-guide"
            >
              <Share size={14} /> Show me how
            </button>
            {showIOSGuide && (
              <ol className="mt-3 text-xs text-zinc-300 space-y-1.5 list-decimal pl-5 leading-relaxed">
                <li>Tap the <b>Share</b> icon at the bottom of Safari (⎋)</li>
                <li>Scroll down and tap <b>Add to Home Screen</b></li>
                <li>Tap <b>Add</b> in the top-right</li>
                <li>Open Panda X from the new home-screen icon</li>
                <li>Come back to Notifications → tap <b>Enable</b></li>
              </ol>
            )}
          </>
        ) : (
          <button
            onClick={install}
            className="mt-3 w-full bg-rose-600 hover:bg-rose-500 text-white font-semibold rounded-md py-2 text-sm flex items-center justify-center gap-2"
            data-testid="pwa-install-button"
          >
            <Download size={14} /> Install app
          </button>
        )}
      </div>
    </div>
  );
}
